import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../axiosinstance";
import API_URL from "../Config";
import { useNavigate } from "react-router-dom";
import { IoIosSearch } from "react-icons/io";
import { LuPlus } from "react-icons/lu";
import { PiSortAscendingThin, PiX } from "react-icons/pi";
import { FiEye } from "react-icons/fi";
import { GoDownload } from "react-icons/go";
import Pagination from "../Components/Pagination";
import "./Table.css";

const FeeCalculations = ({ isSidebarExpanded }) => {
  const [agreements, setAgreements] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isSortPopup, setIsSortPopup] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "created_at",
    direction: "desc",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);

  const navigate = useNavigate();
  const containerRef = useRef(null);

  useEffect(() => {
    const fetchAgreements = async () => {
      setIsLoading(true);
      try {
        const response = await axiosInstance.get("/api/service_agreements");
        const rows = Array.isArray(response.data?.data) ? response.data.data : [];
        setAgreements(rows);
      } catch (error) {
        console.error("Error fetching service agreements:", error);
        setAgreements([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgreements();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim().toLowerCase());
    }, 400);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsSortPopup(false);
      }
    };

    if (isSortPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSortPopup]);

  const getComparableValue = (agreement, key) => {
    if (key === "agreement_no") return (agreement.agreement_no || "").toLowerCase();
    if (key === "client_name") {
      return (agreement.client_snapshot?.name || "").toLowerCase();
    }
    if (key === "entity_type") {
      return (agreement.client_snapshot?.entity_type || "").toLowerCase();
    }
    if (key === "final_total") return Number(agreement.final_total || 0);
    if (key === "created_at") return new Date(agreement.created_at || 0).getTime();
    return "";
  };

  const filteredAndSortedAgreements = useMemo(() => {
    const filtered = agreements.filter((agreement) => {
      if (!debouncedSearch) return true;

      const agreementNo = agreement.agreement_no || "";
      const clientName = agreement.client_snapshot?.name || "";
      const entityType = agreement.client_snapshot?.entity_type || "";

      return [agreementNo, clientName, entityType]
        .join(" ")
        .toLowerCase()
        .includes(debouncedSearch);
    });

    const sorted = [...filtered].sort((a, b) => {
      const aValue = getComparableValue(a, sortConfig.key);
      const bValue = getComparableValue(b, sortConfig.key);

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [agreements, debouncedSearch, sortConfig]);

  const paginatedAgreements = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return filteredAndSortedAgreements.slice(start, start + itemsPerPage);
  }, [filteredAndSortedAgreements, page, itemsPerPage]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    setIsSortPopup(false);
  };

  const handlePaginationChange = (newPage, perPage) => {
    setPage(newPage);
    setItemsPerPage(perPage);
  };

  const formatCurrency = (value) => {
    const amount = Number(value || 0);
    return `Rs ${amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (value) => {
    if (!value) return "NA";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "NA";
    return parsed.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handlePreview = (agreementId) => {
    window.open(
      `${API_URL}/api/service-agreement/${agreementId}/preview`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleDownload = async (agreementId, agreementNo) => {
    try {
      const response = await axiosInstance.get(
        `/api/service-agreement/${agreementId}/download`,
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `${agreementNo || "service-agreement"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Error downloading service agreement:", error);
    }
  };

  return (
    <div
      className={`table-container fee-calculations-page ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="table-container-content fee-calculations-content">
      <div className="table-header">
        <div className="table-header-content">
          <div className="table-left-side">
            <h2 className="jost-bold">Fee Calculator</h2>
          </div>
          <div className="table-right-side">
            <div className="search-bar-container">
              <input
                type="text"
                placeholder="Search ..."
                className="search-bar"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <IoIosSearch className="search-bar-icon" />
            </div>

            <div className="new-button" onClick={() => navigate("/Fee-Calculator/new")}>
              <span className="new-icon">
                <LuPlus />
              </span>
              <span>New</span>
            </div>

            <button
              className="fee-calculations-sort-button"
              onClick={(e) => {
                e.stopPropagation();
                setIsSortPopup((prev) => !prev);
              }}
            >
              <span className="action-icon custom-action-icon-size">
                <PiSortAscendingThin />
              </span>
            </button>

            {isSortPopup && (
              <div className="sort-popup-container" ref={containerRef}>
                <div className="sort-popup-header">
                  <span className="sort-title">Sort By</span>
                </div>
                <div className="sort-popup-content">
                  <button className="sort-button" onClick={() => handleSort("agreement_no")}>
                    Agreement No
                  </button>
                  <button className="sort-button" onClick={() => handleSort("client_name")}>
                    Client Name
                  </button>
                  <button className="sort-button" onClick={() => handleSort("final_total")}>
                    Final Total
                  </button>
                  <button className="sort-button" onClick={() => handleSort("created_at")}>
                    Created Date
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="data-list-body ledger-data-list-body fee-calculations-data-body pb0">
        <div className="data-list-scroll-body ledger-scroll-body fee-calculations-scroll-body" style={{padding: "0px"}}> 
          {/* <div className="data-list-table"> */}
            <table className="table multilevel-data fee-calculations-table">
              <thead className="sticky-header">
                <tr className="table-row">
                  <th>Sr. No</th>
                  <th>Agreement No</th>
                  <th>Client Name</th>
                  <th>Entity Type</th>
                  <th>Turnover</th>
                  <th>Final Total</th>
                  <th>Created On</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="8" className="center-align">
                      Loading fee calculations...
                    </td>
                  </tr>
                ) : paginatedAgreements.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="center-align">
                      No Fee Calculations Found
                    </td>
                  </tr>
                ) : (
                  paginatedAgreements.map((agreement, index) => (
                    <tr key={agreement.agreement_id} className="hoverable-row">
                      <td className="center-align">{(page - 1) * itemsPerPage + index + 1}</td>
                      <td className="fee-agreement-cell">{agreement.agreement_no || "NA"}</td>
                      <td className="fee-client-cell">{agreement.client_snapshot?.name || "NA"}</td>
                      <td>
                        <span className="fee-entity-badge">
                          {agreement.client_snapshot?.entity_type || "NA"}
                        </span>
                      </td>
                      <td className="center-align fee-turnover-cell">{formatCurrency(agreement.turnover)}</td>
                      <td className="center-align fee-final-total-cell">{formatCurrency(agreement.final_total)}</td>
                      <td className="center-align fee-created-cell">{formatDate(agreement.created_at)}</td>
                      <td className="center-align">
                        <div className="action-btn">
                          <span
                            className="action-icon"
                            title="Preview"
                            onClick={() => handlePreview(agreement.agreement_id)}
                          >
                            <FiEye />
                          </span>
                          <span
                            className="action-icon"
                            title="Download"
                            onClick={() =>
                              handleDownload(agreement.agreement_id, agreement.agreement_no)
                            }
                          >
                            <GoDownload />
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          {/* </div> */}
        </div>
        <Pagination
          total={filteredAndSortedAgreements.length}
          showPerPage={itemsPerPage}
          onPaginationChange={handlePaginationChange}
          className="fee-calculations-pagination"
        />
      </div>
      </div>
    </div>
  );
};

export default FeeCalculations;
